import { useState, useEffect, type FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { fetchApi } from '@/lib/api'
import { useAuth } from '@/contexts/AuthContext'
import { Card } from '@/components/Card'
import { Plus, Trash2, ToggleLeft, ToggleRight, X, Copy, Check, Pencil } from 'lucide-react'
import { formatDateTime } from '@/lib/date'

type ApiKeyRow = {
  id: number
  name: string
  key_prefix: string
  permissions: string[]
  is_active: boolean
  expires_at: string | null
  last_used_at: string | null
  created_at: string
  updated_at: string
  user_id: number
  user_name: string
  user_email: string
}

type UserOption = {
  id: number
  name: string
  email: string
}

type CreateForm = {
  name: string
  user_id: number | ''
  permissions: string[]
  expires_at: string
}

const emptyForm: CreateForm = { name: '', user_id: '', permissions: [], expires_at: '' }

const PERMISSION_LABELS: Record<string, string> = {
  'release:read': 'Read (History, Config)',
  'release:start': 'Release (Start, Resume)',
  'release:dry_run': 'Dry-run',
  'release:cancel': 'Cancel',
  'release:*': 'All Release permissions',
  'ops:read': 'Ops Plan Context (Read)',
  'ops:*': 'All Ops permissions',
}

export function ApiKeys() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [keys, setKeys] = useState<ApiKeyRow[]>([])
  const [users, setUsers] = useState<UserOption[]>([])
  const [availablePerms, setAvailablePerms] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState<CreateForm>(emptyForm)
  const [error, setError] = useState('')
  const [submitting, setSubmitting] = useState(false)

  // Created key display
  const [createdKey, setCreatedKey] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)

  // Edit permissions
  const [editKey, setEditKey] = useState<ApiKeyRow | null>(null)
  const [editPerms, setEditPerms] = useState<string[]>([])
  const [editError, setEditError] = useState('')
  const [editSubmitting, setEditSubmitting] = useState(false)

  useEffect(() => {
    if (!user?.is_admin) {
      navigate('/dashboard', { replace: true })
      return
    }
    loadData()
  }, [user, navigate])

  async function loadData() {
    setLoading(true)
    try {
      const [keysData, usersData, permsData] = await Promise.all([
        fetchApi<ApiKeyRow[]>('/api-keys/'),
        fetchApi<UserOption[]>('/users/'),
        fetchApi<string[]>('/api-keys/permissions'),
      ])
      setKeys(keysData)
      setUsers(usersData)
      setAvailablePerms(permsData)
    } catch {
      // handled by api.ts 401 redirect
    } finally {
      setLoading(false)
    }
  }

  function openCreate() {
    setForm(emptyForm)
    setError('')
    setCreatedKey(null)
    setCopied(false)
    setShowModal(true)
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setError('')
    setSubmitting(true)

    try {
      const result = await fetchApi<{ id: number; key: string; key_prefix: string }>('/api-keys/', {
        method: 'POST',
        body: JSON.stringify({
          name: form.name,
          user_id: form.user_id,
          permissions: form.permissions,
          expires_at: form.expires_at || null,
        }),
      })
      setCreatedKey(result.key)
      setCopied(false)
      await loadData()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create API key')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleToggle(key: ApiKeyRow) {
    try {
      await fetchApi(`/api-keys/${key.id}/toggle`, { method: 'PUT' })
      await loadData()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to toggle key')
    }
  }

  async function handleDelete(key: ApiKeyRow) {
    if (!confirm(`Delete API key "${key.name}" (${key.key_prefix})?`)) return
    try {
      await fetchApi(`/api-keys/${key.id}`, { method: 'DELETE' })
      await loadData()
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to delete key')
    }
  }

  async function copyKey() {
    if (!createdKey) return
    await navigator.clipboard.writeText(createdKey)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  function togglePermission(perm: string) {
    setForm(prev => {
      const perms = togglePermInList(prev.permissions, perm)
      return { ...prev, permissions: perms }
    })
  }

  function toggleEditPermission(perm: string) {
    setEditPerms(prev => togglePermInList(prev, perm))
  }

  function togglePermInList(current: string[], perm: string): string[] {
    // Group wildcards: detect "namespace:*" patterns
    const parts = perm.split(':')
    const namespace = parts[0]
    const isWildcard = parts[1] === '*'

    if (isWildcard) {
      if (current.includes(perm)) {
        return current.filter(p => p !== perm)
      }
      // Select wildcard → remove all specific perms in same namespace
      return [...current.filter(p => !p.startsWith(namespace + ':')), perm]
    }

    // Specific perm: remove wildcard if present
    let perms = current.filter(p => p !== `${namespace}:*`)
    if (perms.includes(perm)) {
      perms = perms.filter(p => p !== perm)
    } else {
      perms.push(perm)
    }
    return perms
  }

  function openEditPermissions(key: ApiKeyRow) {
    setEditKey(key)
    setEditPerms([...key.permissions])
    setEditError('')
  }

  async function handleEditSubmit(e: FormEvent) {
    e.preventDefault()
    if (!editKey) return
    setEditError('')
    setEditSubmitting(true)

    try {
      await fetchApi(`/api-keys/${editKey.id}/permissions`, {
        method: 'PUT',
        body: JSON.stringify({ permissions: editPerms }),
      })
      setEditKey(null)
      await loadData()
    } catch (err) {
      setEditError(err instanceof Error ? err.message : 'Failed to update permissions')
    } finally {
      setEditSubmitting(false)
    }
  }

  // Group permissions by namespace for display
  function groupedPerms(perms: string[]): Map<string, string[]> {
    const groups = new Map<string, string[]>()
    for (const p of perms) {
      const ns = p.split(':')[0]
      if (!groups.has(ns)) groups.set(ns, [])
      groups.get(ns)!.push(p)
    }
    return groups
  }

  if (loading) {
    return <div className="p-6 text-[var(--color-text-muted)]">Loading...</div>
  }

  const grouped = groupedPerms(availablePerms)

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-[var(--color-text)]">API Keys</h2>
        <button
          onClick={openCreate}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded bg-[var(--color-primary)] text-white text-sm hover:opacity-90 transition-opacity"
        >
          <Plus size={16} /> Create Key
        </button>
      </div>

      <Card>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-[var(--color-border)] text-[var(--color-text-muted)] text-left">
                <th className="py-2 px-3 font-medium">Name</th>
                <th className="py-2 px-3 font-medium">User</th>
                <th className="py-2 px-3 font-medium">Key</th>
                <th className="py-2 px-3 font-medium">Permissions</th>
                <th className="py-2 px-3 font-medium">Status</th>
                <th className="py-2 px-3 font-medium">Last Used</th>
                <th className="py-2 px-3 font-medium">Created</th>
                <th className="py-2 px-3 font-medium w-28">Actions</th>
              </tr>
            </thead>
            <tbody>
              {keys.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-8 text-center text-[var(--color-text-muted)]">
                    No API keys yet
                  </td>
                </tr>
              )}
              {keys.map((k) => (
                <tr key={k.id} className="border-b border-[var(--color-border)] last:border-0">
                  <td className="py-2 px-3 text-[var(--color-text)]">{k.name}</td>
                  <td className="py-2 px-3 text-[var(--color-text-muted)]">{k.user_name}</td>
                  <td className="py-2 px-3">
                    <code className="text-xs bg-[var(--color-bg)] px-1.5 py-0.5 rounded">
                      {k.key_prefix}
                    </code>
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex flex-wrap gap-1">
                      {k.permissions.map((p) => (
                        <span
                          key={p}
                          className="text-xs px-1.5 py-0.5 rounded bg-blue-500/20 text-blue-400"
                        >
                          {p}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="py-2 px-3">
                    <span className={`text-xs px-2 py-0.5 rounded ${k.is_active ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                      {k.is_active ? 'Active' : 'Inactive'}
                    </span>
                    {k.expires_at && (
                      <div className="text-xs text-[var(--color-text-muted)] mt-0.5">
                        Expires: {formatDateTime(k.expires_at)}
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-3 text-[var(--color-text-muted)]">
                    {k.last_used_at ? formatDateTime(k.last_used_at) : 'Never'}
                  </td>
                  <td className="py-2 px-3 text-[var(--color-text-muted)]">
                    {formatDateTime(k.created_at)}
                  </td>
                  <td className="py-2 px-3">
                    <div className="flex gap-1">
                      <button
                        onClick={() => openEditPermissions(k)}
                        title="Edit permissions"
                        className="p-1 rounded text-[var(--color-text-muted)] hover:text-blue-400 hover:bg-blue-500/10"
                      >
                        <Pencil size={14} />
                      </button>
                      <button
                        onClick={() => handleToggle(k)}
                        title={k.is_active ? 'Disable' : 'Enable'}
                        className="p-1 rounded text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-bg-hover)]"
                      >
                        {k.is_active ? <ToggleRight size={14} /> : <ToggleLeft size={14} />}
                      </button>
                      <button
                        onClick={() => handleDelete(k)}
                        title="Delete"
                        className="p-1 rounded text-[var(--color-text-muted)] hover:text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Edit Permissions Modal */}
      {editKey && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-[var(--color-text)]">
                Edit Permissions
              </h3>
              <button onClick={() => setEditKey(null)} className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]">
                <X size={18} />
              </button>
            </div>

            <div className="mb-4 text-sm text-[var(--color-text-muted)]">
              <span className="text-[var(--color-text)]">{editKey.name}</span>
              {' '}({editKey.key_prefix})
            </div>

            {editError && (
              <div className="mb-4 p-2 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {editError}
              </div>
            )}

            <form onSubmit={handleEditSubmit} className="space-y-3">
              <div>
                <label className="block text-sm text-[var(--color-text-muted)] mb-2">Permissions</label>
                <div className="space-y-3">
                  {[...grouped.entries()].map(([ns, perms]) => (
                    <div key={ns}>
                      <div className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wide mb-1">
                        {ns}
                      </div>
                      <div className="space-y-1.5 ml-1">
                        {perms.map((perm) => (
                          <label key={perm} className="flex items-center gap-2 text-sm text-[var(--color-text)]">
                            <input
                              type="checkbox"
                              checked={editPerms.includes(perm)}
                              onChange={() => toggleEditPermission(perm)}
                              className="rounded"
                            />
                            <code className="text-xs">{perm}</code>
                            <span className="text-[var(--color-text-muted)]">
                              {PERMISSION_LABELS[perm] ? `- ${PERMISSION_LABELS[perm]}` : ''}
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <button
                  type="submit"
                  disabled={editSubmitting || editPerms.length === 0}
                  className="flex-1 py-2 rounded bg-[var(--color-primary)] text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
                >
                  {editSubmitting ? 'Saving...' : 'Save Permissions'}
                </button>
                <button
                  type="button"
                  onClick={() => setEditKey(null)}
                  className="px-4 py-2 rounded border border-[var(--color-border)] text-[var(--color-text-muted)] text-sm hover:bg-[var(--color-bg-hover)] transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create / Created Key Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-semibold text-[var(--color-text)]">
                {createdKey ? 'API Key Created' : 'Create API Key'}
              </h3>
              <button onClick={() => setShowModal(false)} className="text-[var(--color-text-muted)] hover:text-[var(--color-text)]">
                <X size={18} />
              </button>
            </div>

            {/* Show created key */}
            {createdKey ? (
              <div className="space-y-4">
                <div className="p-3 rounded bg-yellow-500/10 border border-yellow-500/30 text-yellow-400 text-sm">
                  Copy this key now. It will not be shown again.
                </div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-[var(--color-bg)] px-3 py-2 rounded border border-[var(--color-border)] text-[var(--color-text)] break-all select-all">
                    {createdKey}
                  </code>
                  <button
                    onClick={copyKey}
                    className="p-2 rounded border border-[var(--color-border)] text-[var(--color-text-muted)] hover:text-[var(--color-text)] hover:bg-[var(--color-bg-hover)] shrink-0"
                    title="Copy"
                  >
                    {copied ? <Check size={16} className="text-green-400" /> : <Copy size={16} />}
                  </button>
                </div>
                <button
                  onClick={() => setShowModal(false)}
                  className="w-full py-2 rounded bg-[var(--color-primary)] text-white text-sm font-medium hover:opacity-90 transition-opacity"
                >
                  Done
                </button>
              </div>
            ) : (
              <>
                {error && (
                  <div className="mb-4 p-2 rounded bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-3">
                  <div>
                    <label className="block text-sm text-[var(--color-text-muted)] mb-1">Name</label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      placeholder="e.g. CI/CD Pipeline"
                      required
                      className="w-full px-3 py-2 rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] text-sm focus:outline-none focus:border-[var(--color-primary)]"
                    />
                  </div>

                  <div>
                    <label className="block text-sm text-[var(--color-text-muted)] mb-1">User</label>
                    <select
                      value={form.user_id}
                      onChange={(e) => setForm({ ...form, user_id: e.target.value ? Number(e.target.value) : '' })}
                      required
                      className="w-full px-3 py-2 rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] text-sm focus:outline-none focus:border-[var(--color-primary)]"
                    >
                      <option value="">Select user...</option>
                      {users.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.name} ({u.email})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm text-[var(--color-text-muted)] mb-2">Permissions</label>
                    <div className="space-y-3">
                      {[...grouped.entries()].map(([ns, perms]) => (
                        <div key={ns}>
                          <div className="text-xs font-medium text-[var(--color-text-muted)] uppercase tracking-wide mb-1">
                            {ns}
                          </div>
                          <div className="space-y-1.5 ml-1">
                            {perms.map((perm) => (
                              <label key={perm} className="flex items-center gap-2 text-sm text-[var(--color-text)]">
                                <input
                                  type="checkbox"
                                  checked={form.permissions.includes(perm)}
                                  onChange={() => togglePermission(perm)}
                                  className="rounded"
                                />
                                <code className="text-xs">{perm}</code>
                                <span className="text-[var(--color-text-muted)]">
                                  {PERMISSION_LABELS[perm] ? `- ${PERMISSION_LABELS[perm]}` : ''}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm text-[var(--color-text-muted)] mb-1">
                      Expiration (optional)
                    </label>
                    <input
                      type="datetime-local"
                      value={form.expires_at}
                      onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                      className="w-full px-3 py-2 rounded border border-[var(--color-border)] bg-[var(--color-bg)] text-[var(--color-text)] text-sm focus:outline-none focus:border-[var(--color-primary)]"
                    />
                  </div>

                  <div className="flex gap-2 pt-2">
                    <button
                      type="submit"
                      disabled={submitting || form.permissions.length === 0}
                      className="flex-1 py-2 rounded bg-[var(--color-primary)] text-white text-sm font-medium hover:opacity-90 disabled:opacity-50 transition-opacity"
                    >
                      {submitting ? 'Creating...' : 'Create Key'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowModal(false)}
                      className="px-4 py-2 rounded border border-[var(--color-border)] text-[var(--color-text-muted)] text-sm hover:bg-[var(--color-bg-hover)] transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
